<template>
  <div>
    <vf-button v-if="isEditingMode" :size="buttonSizes.large" @click="show()">
      {{ $t('patchNotesEditor.createPatchNote') }}
    </vf-button>

    <modal-wrapper
      ref="modalRef"
      :busy="isSaving"
      :close-on-ok-click="false"
      :display-footer="isEditingMode"
      :ok-button-disabled="isSaveButtonDisabled"
      :ok-button-label="saveButtonLabel"
      :size="ModalSize.XL"
      @ok-button-click="savePatchNote"
      @show="handleModalOpened"
    >
      <div class="modal-content-container">
        <div class="modal-top-section">
          <!-- Patch note form for edit mode -->
          <div v-if="isEditingMode" class="patch-note-form-container">
            <!-- Version input -->
            <div class="form-field">
              <label for="version">{{ $t('patchNotesEditor.version') }}</label>
              <div class="version-input-container">
                <PVInputNumber
                  v-model="selectedVersionObject.major"
                  :input-style="{ width: '4rem' }"
                  :max="9"
                  :min="0"
                  @value-change="handleVersionInput"
                />
                <span class="dot">.</span>
                <PVInputNumber
                  v-model="selectedVersionObject.minor"
                  :input-style="{ width: '4rem' }"
                  :max="9"
                  :min="0"
                  @value-change="handleVersionInput"
                />
                <span class="dot">.</span>
                <PVInputNumber
                  v-model="selectedVersionObject.patch"
                  :input-style="{ width: '4rem' }"
                  :max="9"
                  :min="0"
                  @value-change="handleVersionInput"
                />
              </div>
            </div>
            <!-- Existing version hint -->
            <div
              v-if="isExistingVersionHintVisible"
              class="existing-version-hint"
            >
              <div class="hint-text">
                {{
                  $t('patchNotesEditor.existingVersionHint', {
                    version: selectedVersionString,
                  })
                }}
              </div>
              <div class="hint-buttons">
                <div
                  class="hint-button"
                  @click="handleExistingVersionHintConfirmation"
                >
                  {{ $t('patchNotesEditor.editExistingVersion') }}
                </div>
                <div
                  class="hint-button"
                  @click="handleExistingVersionHintDismiss"
                >
                  {{ $t('patchNotesEditor.continueEditing') }}
                </div>
              </div>
            </div>
            <!-- Release date input -->
            <div class="form-field">
              <label for="releaseDate">{{
                $t('patchNotesEditor.releaseDate')
              }}</label>
              <PVDatePicker v-model="releaseDate" :invalid="!releaseDate" />
            </div>
          </div>

          <!-- Modal title for readonly mode -->
          <div v-else>
            <h1>{{ $t('patchNotesEditor.modalTitle') }}</h1>
          </div>

          <!-- Version selection -->
          <div class="version-selection-container">
            <div class="current-version-label">
              {{ $t('patchNotesEditor.currentVersion') }}:
              {{ currentAppVersion }}
            </div>
            <div class="form-field">
              <label v-if="isEditingMode"
                >{{ $t('patchNotesEditor.editExisingPatchNote') }}
                <span
                  v-if="isFormDirty"
                  v-tooltip="t('patchNotesEditor.unsavedChangesHint')"
                  >⚠️
                </span>
              </label>
              <label v-else-if="isViewingMode">{{
                $t('patchNotesEditor.selectVersion')
              }}</label>
              <styled-multiselect
                v-model="selectedExistingVersion"
                :label="versionSelectLabel"
                :options="allVersions"
                :options-limit="15"
                :placeholder="$t('patchNotesEditor.selectVersion')"
                track-by="version"
                @update:model-value="handleExistingVersionSelected"
              />
            </div>
          </div>
        </div>

        <div v-if="isPatchNoteForCurrentVersionMissing">
          ⚠️
          {{ $t('patchNotesEditor.patchNoteForCurrentVersionMissing') }}
        </div>

        <!-- Patch note content input -->
        <div v-else class="form-field">
          <label v-if="isEditingMode" for="content">{{
            $t('patchNotesEditor.content')
          }}</label>
          <v-md-editor
            id="content"
            v-model="content"
            height="400px"
            :mode="markdownEditorMode"
            left-toolbar="h bold italic strikethrough quote | ul ol table hr"
          ></v-md-editor>
        </div>
      </div>
    </modal-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import StyledMultiselect from '@/components/StyledMultiselect/StyledMultiselect.vue';
  import { formatDateForDisplay, isCustomErrorCodeException } from '@/helpers';
  import { PatchNotesService } from '@/services';
  import {
    ErrorCode,
    IGetPatchNotesResponseDto,
    PatchNoteVersion,
  } from 'types';
  import { extractIsoDateString } from 'utils';
  import { PatchNotesEditorMode } from './PatchNotesEditor.types';
  import { useModalWrapper } from '../ModalWrapper';
  import { ModalSize } from '../ModalWrapper/ModalWrapper.types';

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();

  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  const { mode = PatchNotesEditorMode.VIEW } = defineProps<{
    mode?: PatchNotesEditorMode;
  }>();

  const allVersions = ref<PatchNoteVersion[]>([]);
  const content = ref('');
  const currentAppVersion = ref('');
  const fetchedUnmodifiedPatchNote = ref<IGetPatchNotesResponseDto | null>(
    null,
  );
  const isExistingVersionHintVisible = ref(false);
  const isFetchingCurrentAppVersion = ref(false);
  const isFetchingPatchNote = ref(false);
  const isFetchingVersions = ref(false);
  const isPatchNoteForCurrentVersionMissing = ref(false);
  const isSaving = ref(false);
  const releaseDate = ref<Date | undefined>(undefined);
  const selectedExistingVersion = ref<PatchNoteVersion | null>(null);
  const selectedVersionObject = ref({
    major: 0,
    minor: 0,
    patch: 0,
  });
  const wasModalOpened = ref(false);

  const saveButtonLabel = computed(() => {
    return selectedExistingVersion.value
      ? t('patchNotesEditor.saveChangesForVersion', {
          version: selectedVersionString.value,
        }).toString()
      : t('generic.save').toString();
  });

  const selectedVersionString = computed(() => {
    return PatchNotesService.constructVersionString(
      selectedVersionObject.value,
    );
  });

  const isFormValid = computed(() => {
    return !!(
      releaseDate.value &&
      content.value &&
      selectedVersionString.value !== '0.0.0'
    );
  });

  const isFormDirty = computed(() => {
    const isReleaseDateDirty =
      !!releaseDate.value &&
      releaseDate.value !== fetchedUnmodifiedPatchNote.value?.releaseDate;
    const isContentDirty =
      !!content.value &&
      content.value !== fetchedUnmodifiedPatchNote.value?.content;

    return isEditingMode.value && (isReleaseDateDirty || isContentDirty);
  });

  const isEditingMode = computed(() => {
    return mode === PatchNotesEditorMode.EDIT;
  });

  const isViewingMode = computed(() => {
    return mode === PatchNotesEditorMode.VIEW;
  });

  const isSaveButtonDisabled = computed(() => {
    return !isFormValid.value || !isFormDirty.value;
  });

  const markdownEditorMode = computed(() => {
    switch (mode) {
      case PatchNotesEditorMode.EDIT:
        return 'editable';
      case PatchNotesEditorMode.VIEW:
        return 'preview';
      default:
        throw new Error('Invalid mode', mode);
    }
  });

  function handleExistingVersionSelected(version: PatchNoteVersion): void {
    isPatchNoteForCurrentVersionMissing.value = false;
    fetchAndSetPatchNote(version.id);
  }

  async function fetchAndSetPatchNote(id: string): Promise<void> {
    try {
      isFetchingPatchNote.value = true;
      const patchNote = await PatchNotesService.getPatchNoteById(id);
      fetchedUnmodifiedPatchNote.value = patchNote;
      populatePatchNote(patchNote);
    } catch {
      createErrorToast(t('patchNotesEditor.errors.errorGettingPatchNote'));
    } finally {
      isFetchingPatchNote.value = false;
    }
  }

  function clearLoadedVersion(): void {
    selectedExistingVersion.value = null;
    fetchedUnmodifiedPatchNote.value = null;
    releaseDate.value = undefined;
    content.value = '';
  }

  function populatePatchNote(patchNote: IGetPatchNotesResponseDto): void {
    content.value = patchNote.content;
    releaseDate.value = patchNote.releaseDate;
    selectedVersionObject.value = PatchNotesService.splitVersionString(
      patchNote.version,
    );
  }

  function handleVersionInput(): void {
    const existingVersion = allVersions.value.find(
      (version) =>
        version.version ===
        PatchNotesService.constructVersionString(selectedVersionObject.value),
    );
    if (existingVersion) {
      if (isFormDirty.value) {
        isExistingVersionHintVisible.value = true;
      } else {
        fetchAndSetPatchNote(existingVersion.id);
      }
      selectedExistingVersion.value = existingVersion;
    } else {
      isExistingVersionHintVisible.value = false;
      if (!isFormDirty.value) clearLoadedVersion();
    }
  }

  function handleExistingVersionHintConfirmation(): void {
    if (!selectedExistingVersion.value) {
      throw new Error('No existing version selected');
    }
    isExistingVersionHintVisible.value = false;
    fetchAndSetPatchNote(selectedExistingVersion.value.id);
  }

  function handleExistingVersionHintDismiss(): void {
    isExistingVersionHintVisible.value = false;
  }

  async function fetchPatchNoteVersions(): Promise<void> {
    try {
      isFetchingVersions.value = true;
      allVersions.value = await PatchNotesService.getAllPatchNoteVersions();
    } catch {
      createErrorToast(
        t('patchNotesEditor.errors.errorGettingPatchNoteVersions'),
      );
    } finally {
      isFetchingVersions.value = false;
    }
  }

  async function fetchCurrentAppVersion(): Promise<void> {
    try {
      isFetchingCurrentAppVersion.value = true;
      currentAppVersion.value = await PatchNotesService.getCurrentAppVersion();
    } catch {
      createErrorToast(
        t('patchNotesEditor.errors.errorGettingCurrentAppVersion'),
      );
    } finally {
      isFetchingCurrentAppVersion.value = false;
    }
  }

  async function fetchPatchnoteForCurrentVersion(): Promise<void> {
    try {
      isFetchingPatchNote.value = true;
      const patchNote = await PatchNotesService.getPatchNoteForCurrentVersion();
      selectedExistingVersion.value = patchNote;
      populatePatchNote(patchNote);
    } catch (e) {
      if (
        isCustomErrorCodeException(e) &&
        e.response?.data.customErrorCode === ErrorCode.VERSION_NOT_FOUND
      ) {
        isPatchNoteForCurrentVersionMissing.value = true;
      } else {
        createErrorToast(t('patchNotesEditor.errors.errorGettingPatchNote'));
      }
    } finally {
      isFetchingPatchNote.value = false;
    }
  }

  function versionSelectLabel(version: PatchNoteVersion): string {
    return `${version?.version} - ${formatDateForDisplay(
      version?.releaseDate,
    )}`;
  }

  async function savePatchNote(): Promise<void> {
    try {
      isSaving.value = true;

      /** Modifying existing patch note */
      if (selectedExistingVersion.value) {
        await PatchNotesService.updatePatchNote(
          selectedExistingVersion.value.id,
          selectedVersionString.value,
          releaseDate.value!,
          content.value,
        );
      } else {
        /** New patch note */
        await PatchNotesService.createPatchNote(
          selectedVersionString.value,
          releaseDate.value!,
          content.value,
        );
      }

      fetchPatchNoteVersions();
      selectedExistingVersion.value =
        await PatchNotesService.getPatchNoteForVersion(
          selectedVersionString.value,
        );
      fetchAndSetPatchNote(selectedExistingVersion.value.id);

      createSuccessToast(t('patchNotesEditor.patchNoteSuccessfullySaved'));
    } catch (e) {
      if (
        isCustomErrorCodeException(e) &&
        e.response?.data.customErrorCode ===
          ErrorCode.PATCH_NOTE_RELEASE_DATE_CONFLICT
      ) {
        const conflictingDate = new Date(
          extractIsoDateString(e.response?.data.message!)!,
        );
        createErrorToast(
          t('patchNotesEditor.errors.releaseDatesConflict', {
            conflictingDate: formatDateForDisplay(conflictingDate),
          }),
          10 * 1000,
        );
      } else {
        createErrorToast(t('patchNotesEditor.errors.errorSaving'));
      }
    } finally {
      isSaving.value = false;
    }
  }

  async function handleModalOpened(): Promise<void> {
    await fetchCurrentAppVersion();
    /** Fetch data only once when the modal is opened */
    if (!wasModalOpened.value) {
      await fetchPatchNoteVersions();
      if (isViewingMode.value) {
        await fetchPatchnoteForCurrentVersion();
      }
    }
    wasModalOpened.value = true;
  }
</script>
<style lang="scss" scoped>
  $patch-notes-editor-modal-row-gap: 1rem;

  .modal-content-container {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    row-gap: $patch-notes-editor-modal-row-gap;

    .form-field {
      display: flex;
      flex-direction: column;
      row-gap: 0.5rem;
      label {
        font-weight: 500;
        margin-bottom: 0.3rem;
      }
    }

    .modal-top-section {
      display: flex;
      justify-content: space-between;

      .patch-note-form-container {
        max-width: 50%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        row-gap: $patch-notes-editor-modal-row-gap;

        .version-input-container {
          display: flex;

          .dot {
            font-size: 1.5rem;
            line-height: 2;
            vertical-align: bottom;
            margin: 0 0.5rem;
          }
        }

        .existing-version-hint {
          font-size: 0.8rem;

          .hint-buttons {
            display: flex;
            column-gap: 0.5rem;
            text-decoration: underline;
            cursor: pointer;
          }
        }
      }

      .version-selection-container {
        .current-version-label {
          margin-bottom: 1rem;
          font-weight: bold;
        }
      }
    }
  }
</style>
